/**
 * Pure helpers mapping chart display settings to CSS custom properties
 * consumed by AstroLabel.vue's symbol spans. Kept out of the component so
 * the mapping logic is unit-testable with the existing Node-based Vitest
 * setup (no @vue/test-utils / jsdom required).
 */

import { DEFAULT_CHART_DISPLAY_SETTINGS } from './chartDisplaySettings.js';

const CATEGORY_SCALE_KEYS = {
  sign: 'signSymbolScale',
  aspect: 'aspectSymbolScale',
};

/**
 * The single source of truth mapping the global symbolWeight setting to a
 * numeric CSS font-weight. One mapping for every symbol category -- no
 * per-category weight settings, no raw CSS strings stored in settings, no
 * 'bolder'/'lighter' relative keywords.
 */
const SYMBOL_WEIGHT_TO_CSS = {
  normal: 400,
  bold: 700,
};

/**
 * Resolve which scale setting applies to a 'planet' category symbol.
 * Sun and Moon each get their own independent scale; every other
 * planet-category entity (Mercury through Pluto, lunar nodes, asteroids,
 * calculated points) shares planetSymbolScale. Identity comes from the
 * canonical entity id where available; for legacy payloads with no
 * entity_meta, the caller passes the raw display name instead ("Sun"/
 * "Moon"), and this case-insensitive compare treats the canonical id
 * ("sun") and the legacy display name ("Sun") identically -- never a
 * symbol-text or casing heuristic.
 */
function planetScaleKey(canonicalId) {
  const id = typeof canonicalId === 'string' ? canonicalId.toLowerCase() : '';
  if (id === 'sun') {
    return 'sunSymbolScale';
  }
  if (id === 'moon') {
    return 'moonSymbolScale';
  }
  return 'planetSymbolScale';
}

/**
 * Resolve the CSS custom properties for a symbol's weight/scale, given a
 * chart display settings object. Weight always comes from the single
 * global `symbolWeight` setting, regardless of category. Scale is
 * category-dependent, and for the 'planet' category further depends on
 * canonicalId (Sun/Moon get their own scale settings). An unsupported
 * category falls back to 'planet', matching this codebase's existing
 * normalize-to-default (never throw) convention for invalid settings
 * values.
 *
 * @param {'planet' | 'sign' | 'aspect'} category
 * @param {Record<string, unknown>} settings
 * @param {string} [canonicalId] - only consulted for category === 'planet'
 * @returns {{ '--astro-symbol-weight': number, '--astro-symbol-scale': number }}
 */
export function symbolStyleVars(category, settings, canonicalId) {
  const source = settings || {};

  const weightSetting = source.symbolWeight ?? DEFAULT_CHART_DISPLAY_SETTINGS.symbolWeight;
  const weight = SYMBOL_WEIGHT_TO_CSS[weightSetting]
    ?? SYMBOL_WEIGHT_TO_CSS[DEFAULT_CHART_DISPLAY_SETTINGS.symbolWeight];

  const scaleKey = category === 'sign' || category === 'aspect'
    ? CATEGORY_SCALE_KEYS[category]
    : planetScaleKey(canonicalId);
  const scale = source[scaleKey] ?? DEFAULT_CHART_DISPLAY_SETTINGS[scaleKey];

  return {
    '--astro-symbol-weight': weight,
    '--astro-symbol-scale': scale,
  };
}
